import cloneDeep from 'clone-deep';

/**
 * This function uses a 3rd-party library to do its cloning work.
 * The goal of this function is to perform an EXACT deep clone of an
 * object. This means that when cloning a reactive object for example,
 * all the subscribed callbacks will be also cloned, which will gives a
 * still reactive cloned object.
 * 
 * @param source        The source object to clone
 * @returns             A clone of the object
 */
 export function deepClone<T>(source: T): T
 {
     return cloneDeep(source);
 }

/**
 * Instead of performing an exact clone as deepClone() does, this function
 * only deeply copies the values of all properties and all methods of the object
 * which allows to turn a reactive object or a ref into a raw object,
 * discarding all kind of reactivity it had.
 * 
 * @param source        The source object to copy
 * @returns             A deep-copied object
 */
export function deepCopy<T>(source: T): T
{
    // This function is inspired by the following article, but refactored to use less ternaries...
    // https://javascript.plainenglish.io/deep-clone-an-object-and-preserve-its-type-with-typescript-d488c35e5574
    if (Array.isArray(source)) {
        return (source.map(item => deepCopy(item))) as unknown as T;
    }

    if (source instanceof Date) {
        return (new Date(source.getTime())) as unknown as T;
    }

    if (!source || typeof source !== "object") {
        return source;
    }

    return Object.getOwnPropertyNames(source).reduce(
        (o, propName) => {
            const property = Object.getOwnPropertyDescriptor(source, propName);
            if (property !== undefined) {
                Object.defineProperty(o, propName, property);
            }

            o[propName] = deepCopy((source as unknown as {[index:string]:unknown})[propName]);
            return o;
        },
        Object.create(Object.getPrototypeOf(source))
    );
}
